import { useCallback, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type { ViewToken } from 'react-native';
import { Typography, Flex, RemoteImage, List } from '@banhala/ui-kit';
import type { MarketSmallBadgeListProps, MarketSmallBadgeItem } from './types';
import type { AnyObject } from '../../../../types';
import { isNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

const PADDING_HORIZONTAL = 16;
const COLUMN_GAP = 8;
const ROW_GAP = 8;
const ICON_SIZE = 32;
const ITEM_WIDTH = 140;
const ITEM_HEIGHT = ICON_SIZE + 4;

type BadgeEntity = { item: MarketSmallBadgeItem; logging: AnyObject };
type ColumnGroup = BadgeEntity[];

interface MarketSmallBadgeShortIconProps {
  item: MarketSmallBadgeItem;
  logging: AnyObject;
}

const MarketSmallBadgeShortIcon = ({
  item,
  logging,
}: MarketSmallBadgeShortIconProps) => {
  const { name, badge, deeplink } = item;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePress = useCallback(() => {
    if (deeplink) {
      logAnalyticsEvent({
        name: 'CLICK_STORE',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, deeplink, logging, logAnalyticsEvent]);

  return (
    <Flex.Row
      width={ITEM_WIDTH}
      height={ITEM_HEIGHT}
      alignItems="center"
      onPress={handlePress}
    >
      <Flex
        width={ICON_SIZE}
        height={ICON_SIZE}
        borderRadius={ICON_SIZE / 2}
        borderWidth={1}
        borderColor="border_transparent"
        overflow="hidden"
      >
        <RemoteImage uri={badge} width={ICON_SIZE} height={ICON_SIZE} />
      </Flex>

      <Flex flex={1} margin={{ left: 6 }}>
        <Typography type="subtitle2" color="content_primary" numberOfLines={1}>
          {name}
        </Typography>
      </Flex>
    </Flex.Row>
  );
};

const ColumnItem = ({ column }: { column: ColumnGroup }) => (
  <Flex gap={ROW_GAP}>
    {column.map(entity => (
      <MarketSmallBadgeShortIcon
        key={entity.item.sno}
        item={entity.item}
        logging={entity.logging}
      />
    ))}
  </Flex>
);

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 1 };

const keyExtractor = (_: ColumnGroup, index: number) => `column-${index}`;

const renderItem = ({ item }: { item: ColumnGroup }) => (
  <ColumnItem column={item} />
);

export const MarketSmallBadgeList = ({
  entity,
  body,
}: MarketSmallBadgeListProps) => {
  const displayedRef = useRef(new Set<number>());
  const row = body?.row ?? 3;

  const logAnalyticsEvent = useLogAnalyticsEvent();

  const columns = useMemo(() => {
    if (isNil(entity) || entity.length === 0) return [];
    const result: ColumnGroup[] = [];
    for (let i = 0; i < entity.length; i += row) {
      result.push(entity.slice(i, i + row));
    }
    return result;
  }, [entity, row]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      viewableItems.forEach(({ isViewable, item }) => {
        if (!isViewable) return;
        const columnData = item as ColumnGroup;
        columnData.forEach(badgeData => {
          const sno = badgeData.item.sno;
          if (displayedRef.current.has(sno)) return;
          displayedRef.current.add(sno);
          logAnalyticsEvent({
            name: 'DISPLAY_STORE',
            analytics: badgeData.logging.analytics,
            ably_only_log: badgeData.logging.ably_only_log,
          });
        });
      });
    },
    [logAnalyticsEvent],
  );

  if (columns.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={columns}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: PADDING_HORIZONTAL,
    gap: COLUMN_GAP,
    paddingBottom: 16,
  },
});
